export const FACEDOWN = 'facedown';

/**
 * `game.hand` phase values, matching the ENGINE exactly — see
 * apps/engine/games/common/constants.js GAME_HAND, and the identical copies in
 * cash-processor, tournament-processor and the broadcasters.
 *
 * USE THIS MAP for anything that gates on a hand phase. The older `GAME_HAND`
 * further down this file has drifted: it invented two extra names at 0 and 1
 * (DEALER_HAS_BEEN_CHOOSEN, ADJUST_BLINDS), which shifts SETUP_DEALER,
 * SETUP_SMALL_BLIND, SETUP_BIG_BLIND and DEAL_CARDS one below the values the
 * engine actually broadcasts. Only its entries from DEAL_FLOP (6) upward agree
 * with the engine. This map exists because of that drift and is deliberately
 * partial: add entries as call sites need them.
 */
export const GAME_HAND_PHASE = {
  // The dealer-button / blind rotation (engine: table.isRotationAssignment).
  // The engine refuses `returnTable` while `hand` is one of these unless
  // checkIfSittingOut covers the seat's status — see hooks/useImBack.ts.
  GAME_PREP: 0,
  SETUP_DEALER: 1,
  SETUP_SMALL_BLIND: 2,
  SETUP_BIG_BLIND: 3,
  DEAL_CARDS: 4,
  PRE_FLOP_BETTING_ROUND: 5,
  FIND_WINNERS: 13,
  RECORD_NEW_STATS_AND_START_NEW_HAND: 15
} as const;

export const VIEW_SIZE_PERCENTAGE = 1;

export const TIME_PROGRESS_BAR_THICKNESS = 4;
export const AWAIT_BUY_IN_PROGRESS_BAR_THICKNESS = 4;

// CARD SETUP
// Beta feedback: bumped ~8% for readability (was 80×48). Board layout math and
// seat hole-card scaling both derive from these, so positions recompute and the
// aspect ratio (~0.6) is preserved. Iterate from here if more is needed.
export const SMALL_CARD_HEIGHT = 86;
export const SMALL_CARD_WIDTH = 52;

/**
 * How large the community board renders in PORTRAIT, relative to the seats.
 *
 * Portrait felt is narrow and a full-size board crowds the flank seats, so the
 * board is deliberately smaller there. This used to be a bare `scale(0.75)` on
 * the board wrapper — a CONSTANT, so the board did not shrink with the container
 * while the seats did. Since every multi-table grid cell is forced portrait,
 * that is why a grid cell's board and hole cards drifted to visibly different
 * sizes.
 *
 * It is now a RATIO applied to `--seat-scale`, so the board tracks the container
 * like everything else and merely stays smaller than a landscape one.
 *
 * SHARED, and it must stay shared: `useEllipticalPositions` derives the board's
 * half-height from this to decide which bet chips are level with the board. The
 * paint and the clearance maths disagreeing is a whole class of bug — see the
 * retraction in docs/investigations/20260914-table-card-scaling-occlusion.md,
 * where an unqualified comment about this very value cost an investigation.
 */
export const BOARD_PORTRAIT_RATIO = 0.75;

/**
 * The floor the board's scale never drops below, however small the felt gets.
 *
 * The 0.75 above was originally a bare `scale(0.75)` on the board wrapper that
 * ignored the container entirely. Read as a bug — the board did not shrink with
 * the seats — it was also doing real work: it held the community cards at a
 * readable 39px in a multi-table grid cell, where `--seat-scale` is well under
 * 1. Making the board track the container faithfully dropped it to ~29px and
 * made the grid WORSE, which is the opposite of this ticket's purpose.
 *
 * So the board tracks the felt scale UPWARD only: `max(FLOOR, --seat-scale)`.
 * Below a full-size felt it holds the floor; above one it grows like everything
 * else.
 *
 * 1.15 rather than 1, because parity with the old size is not the goal — the
 * community cards being hard to read in a grid is the reported complaint. It
 * paints them at ~45px against the old 39px.
 *
 * THIS IS A FLOOR, NOT THE ANSWER — it is only ever safe because something else
 * bounds it. An earlier version of this comment claimed the headroom was
 * inherent ("~35px of clearance on the narrowest grid cell"). That number came
 * from a model that placed the flank seat at its RAW ring position; production
 * clamps that seat inboard (`seatEdgeSafeLeft`), and on a 390px phone
 * `--seat-scale` never reaches this floor at ANY Table View Size, so the board
 * sat at a constant 0.8625 and crossed the left flank seat's plate by up to
 * 18.2px — the first flop card behind a seat, reported from an iPhone.
 *
 * `components/Table/boardScale.ts` is what makes the floor safe: it applies it
 * and then bounds the result by the room the seats actually leave. Use
 * `resolveBoardScale`, not this constant, to decide how big a board is.
 *
 * Shared with useEllipticalPositions' board-clearance maths, which has to agree
 * about how tall the board actually is or it will place bet chips on it.
 */
export const BOARD_MIN_SCALE_FACTOR = 1.15;

// CARD SOURCE SETUP
export const CARD_SOURCE_POSITION_TOP = 284;
export const CARD_SOURCE_POSITION_LEFT = 624;

// TABLE SETUP
export const TABLE_WIDTH = 1024;
export const TABLE_HEIGHT = 370;

export const TABLE_PADDING_X = 20;
export const TABLE_PADDING_Y = 18;

export const INNER_TABLE_WIDTH = TABLE_WIDTH - TABLE_PADDING_X * 2;
export const INNER_TABLE_HEIGHT = TABLE_HEIGHT - TABLE_PADDING_Y * 2;

export const SEAT_DISTANCE_FROM_TABLE = 15;
export const SEAT_WIDTH = 72;
export const SEAT_HEIGHT = 72;

export const BET_WIDTH = 32;
export const BET_HEIGHT = 32;

// EN-10520 — the dealer button used to render at roughly half a seat's width:
// DealerChip's own `clamp(28px, 8vmin, DEALER_WIDTH + 4)` floor ignored the
// portrait constants entirely, which is what let the marker drift outside the
// seat and paint over revealed hole cards. It is now a small marker sized to
// live INSIDE the seat card — ~36% of SEAT_WIDTH in landscape, ~28% in
// portrait. DealerChip reads these directly; there is no separate clamp.
export const DEALER_WIDTH = 26;
export const DEALER_HEIGHT = 26;
export const DEALER_PORTRAIT_WIDTH = 20;
export const DEALER_PORTRAIT_HEIGHT = 20;
/**
 * Where the dealer marker sits horizontally relative to the seat card's
 * centre-facing edge, in px. NEGATIVE hangs it outside that edge.
 *
 * It started at `+2` (tucked just inside), which read as sitting ON the
 * player's display name. -25 parks it clear of the card entirely, in the gap
 * between the seat and the felt centre. The overhang always points INWARD —
 * left-of-midline seats overhang right, right-of-midline seats overhang left —
 * so it never moves anything closer to a viewport edge (Jason, 2026-08-19).
 */
export const DEALER_SEAT_OFFSET_PX = -25;

/**
 * Painted size of a player's bet pill (chip + amount — see Table/Bet.tsx). Only
 * used to keep the pill clear of the seat furniture; the pill itself is sized by
 * its content, so treat these as a representative "$600"-ish footprint rather
 * than a guarantee.
 */
export const BET_PILL_WIDTH = 42;
export const BET_PILL_HEIGHT = 26;

/** Air left between a bet pill and the seat furniture it has to clear, in px. */
export const FLANK_BET_GAP_PX = 6;

/**
 * Above this topPct the POV seat is in the bottom band — i.e. a portrait/tall
 * table. Landscape puts the bottom seat at ~53%, so the cap never fires there.
 */
export const SELF_BET_BOTTOM_BAND_PCT = 60;

/**
 * The dealer marker's top, measured from the seat card's own top edge.
 *
 * It used to be DERIVED — bottom-aligned to the display-name row — which put it
 * level with the name and left a lower-flank seat's bet pill reading as stuck to
 * it (reported on Bryce and Frank in a 6-max portrait table). It now rests up in
 * the band beside the popped avatar, which is empty: the marker hangs OUTSIDE
 * the card's centre-facing edge (DEALER_SEAT_OFFSET_PX), so it clears the avatar
 * horizontally and the name/balance rows vertically.
 *
 * A flat value rather than a formula, because what it encodes is a resting place
 * on the card — 10px down from its top edge — not a relationship to a row that
 * moves. PORTRAIT ONLY: landscape keeps the original name-row alignment, since
 * its flank bets stay level with the player rather than dropping under the card.
 *
 * This DOES put the marker in the hole-card band's vertical range. On a
 * right-column seat the cards fan across it, and the marker renders behind them
 * (TableZ.withinSeatDealer < TableZ.withinSeatCards) — deliberate, and asserted
 * in the bet-clearance suite.
 */
export const DEALER_SEAT_TOP_PX = 10;

export const OVERALL_WIDTH = TABLE_WIDTH + SEAT_DISTANCE_FROM_TABLE * 2;
export const OVERALL_HEIGHT = TABLE_HEIGHT + SEAT_DISTANCE_FROM_TABLE * 2;

export const BET_PATH_WIDTH = TABLE_WIDTH - TABLE_PADDING_X * 2 - DEALER_WIDTH;
export const BET_PATH_HEIGHT = TABLE_HEIGHT - TABLE_PADDING_Y * 2 - DEALER_HEIGHT;

export const TABLE_TOP_MARGIN = 40;

// ---------------------------------------------------------------------------
// EN-10520 — table experience layout (SkullCrusher pass)
// ---------------------------------------------------------------------------

/**
 * How far the WHOLE table (felt, apron, board column, seat ring, winner banner)
 * sits below its previous centre, as a percentage of the table container's
 * HEIGHT — so it scales with the viewport instead of being a fixed pixel nudge.
 *
 * It was 4.5%, chosen to buy clearance at the TOP corners when the corner seats
 * first moved there. The seat ring has been re-tuned since — the top wedge
 * pushed those seats toward the corners and DOWN, and the bottom wedge moved the
 * POV's neighbours up — so 4.5% became an over-correction, leaving an obviously
 * empty band above the felt (reported 2026-08-25).
 *
 * Measured at the design viewports, counting the avatar that pops 36px above
 * each seat card (which is what makes the top of the ring reach further than the
 * bottom):
 *
 *   shift   top margin   bottom margin
 *   4.5%    17.0%        11.5%     <- the reported imbalance
 *   2%      14.5%        14.0%     <- dead centre
 *   0%      12.5%        16.0%     <- what shipped
 *
 * 0 biases the ring slightly HIGH rather than centring it, which is the call
 * that was made: the band above the felt was the complaint, and the extra room
 * below is spent on the POV player's cards and label rather than left empty.
 * At 0 this constant is inert — the ring sits at its base centre — but it stays
 * as the knob for exactly this adjustment.
 *
 * Anything anchored to the felt has to move WITH it, or it silently detaches the
 * next time this changes: that is how the POV bet pill drifted, twice, back
 * when it was pinned to an absolute percentage of the felt.
 */
export const TABLE_DOWN_SHIFT_PCT = 0;

/**
 * The extra downward push the POV seat gets on top of its ring position, in vh.
 *
 * Shared, because the POV bet pill anchors to that same seat and has to be
 * written in the very same expression — see SELF_BET_ABOVE_SEAT_PX.
 */
export const POV_SEAT_EXTRA_DROP_VH = 2.5;

/**
 * Where the CURRENT PLAYER's bet pill sits: this many px ABOVE the POV seat's
 * own anchor point, before `--seat-scale`.
 *
 * It used to be an absolute percentage of the table container (69.5%), which is
 * the whole reason it kept breaking. The pill and the one thing it has to clear
 * — the POV dealer marker, which hangs off the top of the seat's content box
 * — were positioned in three different unit systems at once:
 *
 *   pill    % of the table CONTAINER
 *   seat    % of the container PLUS POV_SEAT_EXTRA_DROP_VH (% of the VIEWPORT)
 *   marker  px, scaled by `--seat-scale`
 *
 * so the air between pill and marker came out as
 * `a x containerHeight + b x viewportHeight - c x scale`, which is only right at
 * one device size. It was tuned until it looked right on a dev viewport and was
 * reported overlapping on a real phone and on portrait web (2026-09-08) — the
 * third re-tune of this same pair (POV_DEALER_TOP_PX records the earlier two).
 *
 * Anchoring the pill to the SEAT collapses all three to one unit: both are now
 * `seatAnchor - k x scale`, so the air between them is `k x scale` px and holds
 * at every viewport, container height and seat count by construction.
 *
 * The value reproduces the old 69.5% at the 480x900 design size (a 117px lift)
 * plus 12px of real air — at 117 the marker's top edge sat ON the pill's bottom
 * edge, which is why a couple of px of drift was enough to show.
 *
 * It is only a static baseline. When the bet tray opens, Bet.tsx MEASURES the
 * real card and pill rects and lifts the pill to clear them by
 * TRAY_OPEN_PILL_CARD_GAP_PX, so this value does not have to be conservative
 * enough to cover the lifted-card case on its own.
 */
export const SELF_BET_ABOVE_SEAT_PX = 129;

/**
 * The CURRENT PLAYER's dealer marker, in px above its seat row.
 *
 * The POV seat is the one exception to getSeatDealerOffset's rule: it is
 * bottom-centre, so "toward the table centre" is UP, and the marker is centred
 * above the card band rather than beside a name row. The value is negative
 * because the marker is anchored by its BOTTOM edge (translate -100%).
 *
 * -16 put it hard against the bet pill above it; -10 drops it clear. The pill
 * anchors to this same seat now (SELF_BET_ABOVE_SEAT_PX), so the air between
 * the two is one scaled-px quantity rather than a coincidence of viewport.
 */
export const POV_DEALER_TOP_PX = -10;

/**
 * Slice of the seat ring's arc kept EMPTY at top-centre, as a fraction of the
 * whole ring. Slot 0 is still the bottom-centre (POV) seat; every other seat is
 * spread evenly over what is left, so no seat can land at top-centre and the
 * top-most seat on each side is pushed toward its corner.
 *
 * At 0.18 a 9-max portrait table puts its top seats at ~20%/80% horizontally
 * (was ~37%/63%) and ~6% further down the felt — clear of the hamburger / chat
 * / add-chips chrome that owns the top band.
 */
export const SEAT_TOP_CLEARANCE_ARC_FRACTION = 0.18;

/**
 * The same idea at the BOTTOM of the ring: extra arc kept clear either side of
 * the POV seat, so its two neighbours ride further up the flanks.
 *
 * Without it the 9-max flank seats land at ~29%/71% x 81% — directly underneath
 * the POV player's own hole cards, which span roughly 26%-74% for a four-card
 * hand, and underneath the POV dealer marker. Both neighbours were being painted
 * over (reported 2026-08-19 with a 9-max four-card screenshot).
 *
 * 0.10 clears that AND balances the ring: it is the value at which a 9-max
 * portrait table becomes symmetric about its own horizontal axis, so each
 * bottom seat sits as far below the felt's centre as a top one sits above it —
 *
 *   Bryce / Ivy    at 76.0%   (25.5% below the felt's centre)
 *   Esme  / Frank  at 25.0%   (25.5% ABOVE it)
 *   Carla / Hugo   at 60.0%, Diego / Greta at 41.0%   (9.5% either side)
 *
 * The neighbours were previously at 73.4%, closer to the felt's waist than their
 * opposite numbers and visibly unbalanced against them (reported 2026-08-24).
 * Moving them is what this value does; the rest of the ring shifts with it,
 * which is the point — every seat lands level with its mirror.
 *
 * This governs HEIGHT only for the two neighbours: their column is tuned
 * separately, because the ring's bottom curves in harder than the felt does.
 * See PORTRAIT_POV_NEIGHBOUR_COLUMN in useEllipticalPositions.
 *
 * That symmetry is exact at 9-max (the mirror of slot k is slot 5 - k, a whole
 * slot) and approximate at other counts, where the seats-per-side arithmetic
 * does not land on integers.
 */
export const SEAT_BOTTOM_CLEARANCE_ARC_FRACTION = 0.1;

/** Air kept between any seat element and the viewport edge, in px. */
export const SEAT_EDGE_BUFFER_PX = 8;

/**
 * Fraction of a card's width tucked under its right-hand neighbour at showdown.
 * The rank glyph is CENTRED on the card and spans roughly the middle 62% of its
 * width, so overlapping more than ~0.19 starts eating ranks. 0.18 is that
 * readable maximum, and it is what lets four Omaha cards fit inside one seat.
 */
export const SHOWDOWN_CARD_OVERLAP = 0.18;

/**
 * Scale a FACE-DOWN hole card renders at.
 *
 * Portrait was 0.36, which with the old peek showed ~2.8px of each card in a
 * 4-card fan — a dark sliver rather than a hand (reported 2026-08-24 across
 * every opponent seat). 0.5 matches the scale a revealed Omaha hand lands on,
 * so a four-card hand no longer changes size when it flips.
 */
export const HOLE_CARD_SCALE_FACE_DOWN_PORTRAIT = 0.5;
export const HOLE_CARD_SCALE_FACE_DOWN_LANDSCAPE = 0.6;

/**
 * How much of each face-down card its neighbour leaves showing, as a fraction of
 * a card's width.
 *
 * This is what makes a flat pair legible as two cards rather than one dark
 * shape. Omaha does not use it — a four-card hand is splayed instead, see
 * HOLE_CARD_FAN_ROTATION_STEP_DEG.
 */
export const HOLE_CARD_FAN_PEEK_PAIR = 0.35;

/**
 * How far LEFT of the avatar's centre the face-down fan stops, in unscaled px.
 *
 * The fan is allowed to cover the portrait, but not the turn-timer ring drawn
 * around it — the countdown is the one thing on a seat you have to be able to
 * read at a glance (reported 2026-08-25 on Esme, mid-turn).
 *
 * This is a nudge off the avatar's centre line, not full ring clearance: the
 * ring's radius is ~32px and clearing it outright is what made the fan
 * unreadable in the first place. It buys back the timer's inner face while the
 * cards still overlap the ring's outer arc.
 *
 * It is spent from the seat's viewport budget: every px here is a px of left
 * overhang, which tightens the edge clamp (see seatEdgeInsets) and holds the
 * outermost seats further inboard.
 */
export const HOLE_CARD_FAN_TIMER_CLEARANCE_PX = 9;

/**
 * Degrees between neighbouring cards in a face-down OMAHA fan.
 *
 * A four-card hand laid out flat is a wide rectangle, and its bottom-left corner
 * lands on the dealer marker of any right-column seat — the marker hangs off the
 * seat card's centre-facing edge, which is the same side the cards fan to
 * (reported 2026-08-25 on Frank, Greta, Hugo and Ivy).
 *
 * Splaying the cards around a hinge at the bottom-right fixes that by shape
 * rather than by position: the hand is widest at the TOP, where nothing else
 * lives, and narrowest at the bottom, where the marker is. A two-card hand is
 * left flat — it never reached the marker to begin with.
 *
 * 11 degrees is the widest splay that still clears the marker with the hinge on
 * the card's bottom edge, which is the only place the hinge can be (see
 * holeCardFan). It also reaches 56.3px left against the flat spread's 58.4px, so
 * the seat's edge clamp loosens slightly rather than tightening.
 */
export const HOLE_CARD_FAN_ROTATION_STEP_DEG = 11;

/**
 * Scale added to a revealed hand ON TOP of the scale that would fit it inside
 * the seat's own footprint.
 *
 * A pure fit shrinks a 4-card Omaha hand to ~0.40, which is legible but too
 * small to read at a glance on a phone — the ranks are there, you just have to
 * look for them. This buys back a size step (~0.40 -> ~0.50) by letting the
 * spread hang past the seat's footprint.
 *
 * It costs footprint, deliberately: the 4-card spread grows 72px -> 90px, so a
 * reveal now overhangs the 80px seat card by ~5px per side. It stays inside
 * SEAT_CARD_WIDTH / 2 + SEAT_EDGE_BUFFER_PX, which is the air the seat clamp
 * already reserves, so a revealed hand still cannot touch a viewport edge.
 *
 * A 2-card hand is unaffected: it is already at SCALE_FACE_UP, and the cap
 * absorbs the boost.
 */
export const SHOWDOWN_CARD_SCALE_BOOST = 0.1;

/**
 * Vertical anchor for a seat's hole cards, as a fraction of a card's height
 * above the seat card's TOP edge. Face-down and revealed hands share it, which
 * buys two things:
 *
 *  - the card band does not jump when the cards flip at showdown (and because
 *    the card layer is a zero-size absolute anchor, the seat's own box never
 *    grows either way);
 *  - a revealed hand sits OVER the player's portrait rather than under it —
 *    covering the avatar is fine, hiding the cards behind it is not
 *    (Jason, 2026-08-19).
 */
export const SEAT_CARD_BAND_LIFT_PORTRAIT = 0.5;
export const SEAT_CARD_BAND_LIFT_LANDSCAPE = 0.75;

/**
 * Seat card geometry the dealer marker has to line itself up against.
 * `PADDING_TOP` is the band the popped avatar occupies; the display name is the
 * first text row below it, and NAME_ROW_HEIGHT is that row's line box.
 */
export const SEAT_CARD_PADDING_TOP_PORTRAIT = 34;
export const SEAT_CARD_PADDING_TOP_LANDSCAPE = 33;
export const SEAT_NAME_ROW_HEIGHT = 18;

/**
 * The popped avatar's own geometry — how far above the seat card its wrapper is
 * anchored, and the scale applied inside that wrapper.
 *
 * These live here rather than inline because three places need to agree on them:
 * the avatar's own style, the hole-card offsets (which have to clear the timer
 * ring), and the action bubble (which has to sit above the avatar's head). They
 * used to be repeated as bare numbers in each, with a comment asking the next
 * person to keep them in sync.
 */
export const SEAT_AVATAR_SCALE_PORTRAIT = 0.72;
export const SEAT_AVATAR_SCALE_LANDSCAPE = 0.91;
export const SEAT_AVATAR_POP_PORTRAIT = 36;
export const SEAT_AVATAR_POP_LANDSCAPE = 44;

/** Air between the top of the avatar and the bottom of its action bubble. */
export const SEAT_ACTION_BUBBLE_GAP_PX = 2;

/** Rendered floor for seat name / balance text, below which it truncates. */
export const SEAT_TEXT_MIN_FONT_PX = 10;

/**
 * Width of the seat card — the pill carrying the display name and balance.
 *
 * Deliberately its own constant rather than SEAT_CONTAINER_WIDTH: that one is
 * the AVATAR's footprint and is what the timer ring, the flame/action effects,
 * the bounty flight and the showdown card fit all measure against. The card
 * behind the text can be wider than the avatar without disturbing any of them.
 */
export const SEAT_CARD_WIDTH = 80;

/**
 * Floor for the display NAME's font size, in px at Table View Size 1.
 *
 * Much lower than SEAT_TEXT_MIN_FONT_PX because the name never truncates — it
 * keeps shrinking until it fits the card (an ellipsis hides which player you are
 * looking at; a small name does not).
 *
 * 5 is not arbitrary: it is the value at which a 20-character name still fits
 * SEAT_TEXT_MAX_WIDTH at the LARGEST Table View Size (1.2), which is the worst
 * case the fit has to survive — 72 / 1.2 = 60px of room, and 60 / (20 chars ×
 * AVG_GLYPH_WIDTH_RATIO) = 5.17. Real names are far shorter and never reach it;
 * a 12-character name still renders around 9px at that same setting. Beyond ~20
 * characters the floor holds and `overflow: hidden` clips the tail.
 */
export const SEAT_NAME_MIN_FONT_PX = 5;

/**
 * The ONLY limit left on how small the measured fit may take a display name.
 *
 * Product decision 2026-09-17: the seat display name never truncates, full stop.
 * Every previous floor existed to keep the name legible and accepted an ellipsis
 * when it could not; that trade is now reversed, so the floor may not win over
 * showing the whole name. This exists purely so a font size cannot reach zero or
 * go negative — it is arithmetic safety, not a design size.
 *
 * `SEAT_NAME_MIN_FONT_PX` (5) stays as it was: it is still the floor for the
 * legacy PREDICTED helper `fitFontSizePx`, which is a different mechanism and is
 * not what sizes the seat name any more.
 */
export const SEAT_NAME_ABSOLUTE_MIN_PX = 1;

/**
 * How small the display NAME may actually shrink, in painted px.
 *
 * Distinct from SEAT_TEXT_MIN_FONT_PX, which the name row uses as the size it
 * STARTS at: a landscape seat paints at ~0.56, so an 11.5px design size would
 * otherwise land at 6.4px and read smaller than the balance directly under it.
 * That start is still lifted to 10 painted px — this is only how far the fit is
 * then allowed to come back down.
 *
 * The two used to be the same number, which meant a name too wide to hold 10
 * painted px stopped shrinking and truncated instead ("DiamondPlayer99…").
 * Product call: a name is an identity, so the whole of it has to stay on screen
 * — the same reasoning SEAT_STACK_MIN_FONT_PX applies to the balance, one row
 * down.
 *
 * ZERO — the floor is DISABLED, and that is the point of it.
 *
 * It was 4, chosen so the fit would outrun the card rather than the floor for
 * any name up to ~17 characters. That held for realistic names, and QA's
 * deliberately pathological fixtures found the gap anyway: `wwwwwwwwwwwww` and
 * `WWWWWWWWWWWW` still landed ON the floor in a multi-table grid cell and
 * truncated (measured in Chromium, 2026-09-17 — a 4-grid at seat scale 0.53
 * needed 3.75 and 3.15 painted px respectively against the 4px floor).
 *
 * Product decision 2026-09-17, asked for repeatedly: the name always renders
 * complete. So there is no painted floor left to compete with fitting, and
 * `SEAT_NAME_ABSOLUTE_MIN_PX` is the only limit that remains.
 *
 * THE TRADE, stated plainly because the next person will wonder: this buys a
 * complete name with a very small one on pathological input. A 20-character run
 * of `W` in the smallest supported grid cell fits at roughly 1.8 painted px —
 * present, but not readable. Real names are nowhere near that: a 12-character
 * name still paints around 6.6px at a single table and 4.0px in a 4-grid.
 *
 * Raising this above zero brings truncation back, and the name row no longer has
 * an ellipsis to fall back on — it would HARD-CLIP mid-glyph instead. If this
 * ever needs a floor again, restore `textOverflow: 'ellipsis'` on
 * `opponentNameBase` and `myPlayerName` in the same change.
 */
export const SEAT_NAME_MIN_PAINTED_PX = 0;

/**
 * Floor for the seat BALANCE's font size, in painted px.
 *
 * The balance shrinks to fit rather than truncating, for a reason the name row
 * does not share: a cut-off money figure is not merely shortened, it is
 * MISLEADING. "$1,670…" reads equally as $1,670.50 and as $1,670,000, and a
 * player sizing a bet against the wrong stack has been given bad information.
 * A name that ends in an ellipsis still identifies its player; a truncated
 * balance identifies nothing.
 *
 * Lower than SEAT_TEXT_MIN_FONT_PX (10) because this row is digits, "$", "," and
 * ".", which stay legible smaller than mixed-case letters do — and because the
 * extra room is what keeps the ellipsis out of reach. A 12-character balance
 * ("$1,234,567.89", the widest real value) fits SEAT_TEXT_MAX_WIDTH at ~9px, so
 * 8 leaves headroom rather than being the working size.
 */
export const SEAT_STACK_MIN_FONT_PX = 8;

/**
 * How small the BALANCE may actually shrink, in painted px.
 *
 * SEAT_STACK_MIN_FONT_PX is the size the balance row STARTS at. Passing it as
 * the floor as well made it a HARD floor, and a value too wide to hold 8 painted
 * px stopped shrinking and truncated instead — the same single-value mistake the
 * NAME row was fixed for on 2026-09-10 (see SEAT_NAME_MIN_PAINTED_PX), left in
 * place one row down.
 *
 * Measured in Chromium at the real seat geometry: with the hard floor,
 * "$1,234,567.89" truncated across seat scales 0.50-0.70 and "$100,000.00"
 * across 0.50-0.69 — ordinary balances, not edge cases. Splitting the bounds
 * takes every one of them to zero.
 *
 * 4 rather than something lower because that is where the fit outruns the card
 * instead of the floor: a 13-character balance still clears SEAT_TEXT_MAX_WIDTH
 * with headroom, so this stays a backstop rather than a working size. A cut-off
 * money figure is MISLEADING, not merely shortened — "$1,670…" reads equally as
 * $1,670.50 and $1,670,000 — which is why the balance buys completeness with
 * size exactly as the name does.
 */
export const SEAT_STACK_MIN_PAINTED_PX = 4;

/**
 * Native pixel size of the felt watermark (assets/images/hjlogo.png). The felt
 * caps its displayed width here so a wide desktop table never upscales — and
 * blurs — the asset.
 */
export const TABLE_LOGO_NATIVE_WIDTH = 200;
export const TABLE_LOGO_NATIVE_HEIGHT = 87;

// SEAT SETUP
export const SEAT_CONTAINER_WIDTH = SEAT_WIDTH;
export const SEAT_CONTAINER_HEIGHT = SEAT_HEIGHT;
export const SEAT_TIMER_SIZE = SEAT_WIDTH + TIME_PROGRESS_BAR_THICKNESS * 2 * 2.25;

// SCREEN BREAKPOINT
export const ALLOWED_BROWSER_MINIMUM_SCREEN_SIZE = 992; // Measured in px

// SPOTLIGHT
export const SPOTLIGHT_HEIGHT = 250;
export const SPOTLIGHT_WIDTH = 250;

// LEFT PANE
export const LEFT_PANE_WIDTH = 350;

// COLORS
export const INSET_WINNER_AVATAR = '#BDFF94';
export const GLOW_OFF_WINNER_AVATAR = 'rgba(98,139,72,0)';
export const GLOW_ON_WINNER_AVATAR = 'rgba(98,139,72,1)';

export const INSET_SPLIT_WINNER_AVATAR = '#FFDF99';
export const GLOW_OFF_SPLIT_WINNER_AVATAR = 'rgba(233,140,38,0)';
export const GLOW_ON_SPLIT_WINNER_AVATAR = 'rgba(233,140,38,1)';

export const INSET_OTHER_WINNERS_AVATAR = '#FFDF99';
export const GLOW_OFF_OTHER_WINNERS_AVATAR = 'rgba(233,140,38,0)';
export const GLOW_ON_OTHER_WINNERS_AVATAR = 'rgba(233,140,38,1)';

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Cash. Matches Unity's TABLE_TYPE_CASH (Constants/GameConstants.cs:7) - 's',
// not 'c'. Several call sites referenced this name in comments before it
// existed and inlined the literal instead.
export const TABLE_TYPE_CASH = 's';
export const TABLE_TYPE_SITNGO = 't';
export const TABLE_TYPE_TOURNAMENT = 'm';

export const MULTI_RUN_VOTING_PREFIX = 'v';

export const BET_PREFIX = {
  FOLD: 'F',
  JOIN: 'J'
};

export const PLAYER_STATUS = {
  ACTIVE: 0,
  SITTING_OUT: 1,
  LEAVING: 2,
  SHOW_CARDS: 3,
  POST_BLIND: 4,
  WAIT_FOR_BB: 5,
  FOR_SITOUT: 6,
  FOR_CHARGING: 7,
  SHOW_CARDS_FOR_SITOUT: 8,
  IMMEDIATE_KICK: 9,
  EXIT_TABLE: 10,
  SHOW_CARDS_FOR_STANDUP: 11,
  BUSTED: 12,
  ADD_FUND: 13,
  KICK_AFTER_CHARGE_TIMER_EXPIRY: 14,
  EXIT_TABLE_WAIT_FOR_BB: 17,
  EXIT_TABLE_POST_BLIND: 18,
  MUCK_CARDS: 19,
  MUCK_CARDS_FOR_SITOUT: 20,
  MUCK_CARDS_FOR_STANDUP: 21,
  SHOW_CARDS_FOR_EXIT: 22
};

export const PLAYER_ACTION = {
  CALL: 'call',
  CHECK: 'check',
  BET: 'bet',
  RAISE: 'raise',
  FOLD: 'fold',
  ALLIN: 'allin',
  STRADDLE: 'straddle'
};

export const PLAYER_PRE_ACTION = {
  CALL_ANY: 'callany',
  CHECK_OR_FOLD: 'checkorfold',
  FOLD: 'fold',
  CHECK: 'check',
  CALL: 'call',
  RAISE: 'raise',
  ALLIN: 'allin'
};

export const GAME_HAND = {
  GAME_NOT_STARTED: '',
  DEALER_HAS_BEEN_CHOOSEN: 0,
  ADJUST_BLINDS: 1,
  SETUP_DEALER: 2,
  SETUP_SMALL_BLIND: 3,
  SETUP_BIG_BLIND: 4,
  DEAL_CARDS: 5,
  DEAL_FLOP: 6,
  FLOP_BETTING_ROUND: 7,
  DEAL_TURN: 8,
  TURN_BETTING_ROUND: 9,
  DEAL_RIVER: 10,
  RIVER_BETTING_ROUND: 11,
  AFTER_RIVER_BETTING_ROUND: 12,
  FIND_WINNERS: 13,
  PAY_WINNERS: 14,
  RECORD_NEW_STATS_AND_START_NEW_HAND: 15
};

export const HAND_TYPE = {
  HI: 'Hi',
  LO: 'Lo'
};

// EN-10420 — the POV decision group (hole cards + hand-strength label) lifts
// clear of the open bet/raise tray. The tray paints over the whole POV seat
// (footerRow's stacking context outranks tableRow — see pages/gamePageLayout.ts),
// so the pieces the player needs mid-decision MOVE out from under it instead of
// fighting z-order. The stack row deliberately does NOT move (product decision,
// Jason 8/12 — the tray carries its own countdown twin, and All In / the slider
// max carry the stack amount).
//
// The TRAVEL itself is measured, not declared: see hooks/useTrayClearanceLift
// for why every viewport-derived formula failed. Only the timing lives here, so
// the cards, the label and the bet pill share one duration.
/** Breathing room between the lifted cards' top edge and the POV bet pill —
 *  one card-gap, per design. The pill's actual travel is MEASURED at open
 *  time from the real elements (Table/Bet + PovCardsContext), because the
 *  pill is percentage-anchored while the cards are seat-anchored: fixed
 *  pixels drifted across device sizes and after rotation. */
export const TRAY_OPEN_PILL_CARD_GAP_PX = 4;
/** Fallback pill travel when the cards element can't be measured (registry
 *  miss). Rough single-table-portrait value; the measured path is primary. */
export const TRAY_OPEN_BET_PILL_LIFT_FALLBACK_PX = 48;
/** Fraction of the FIRST measured travel the pill actually rises (design
 *  choice, Jason 2026-08-10). That first measurement is taken before the
 *  pill's %-anchor re-resolves, so it reads long; the post-glide verifier then
 *  pulls the pill down to the exact gap. Rising the full predicted distance
 *  made that pull-down read as a bounce, so the pill travels most of the way
 *  up and eases down into place. 1 = no damping (rise full, drop far); lower
 *  = shorter rise, gentler drop. 0.75 is the midpoint Jason settled on after
 *  seeing 1 (too much bounce) and 0.5 (rise too short). */
export const TRAY_OPEN_PILL_INITIAL_TRAVEL_FRACTION = 0.75;
/** Glide duration (ms) for the tray-open lifts — cards and bet pill move at
 *  the same speed so they read as one motion. */
export const TRAY_OPEN_CARD_LIFT_MS = 220;

// ---------------------------------------------------------------------------
// EN-10420 — bet/raise tray WIDTH variants (Jason + Marlon huddle 2026-08-13).
//
// ONE rule, both orientations, keyed on viewport width alone: wide enough to
// afford side gutters → a contained rounded panel whose gutters keep the
// floating tournament-utility stack (trophy / add-on) visible and tappable
// while the tray is open; narrower → a full-bleed bar that intentionally
// paints over that stack (tournament details are not decision info during a
// 15s bet window, and on a phone a competing tap target next to the bet
// controls costs more than it gives). Orientation deliberately does NOT
// participate: an iPad held portrait still affords gutters. This supersedes
// the 8/12 always-covered AC note and the old landscape-only 1024px split.
//
// The flip point is DERIVED, not hand-picked:
//   - a contained tray must never render narrower than the 390px design
//     floor (the narrowest supported phone — the full-bleed tray already
//     ships at exactly that internal width there and is the approved look);
//   - each gutter must clear the floating util stack: 8px footer inset +
//     44px icon button + 12px air = 64px.
//   390 + 2×64 = 518. (The tray's structural min-content floor measured
//   246px on 2026-08-13 with the in-tray countdown column present, so the
//   design floor — not the structural one — is the binding constraint.)
export const TRAY_MAX_WIDTH_PX = 832; // 52rem — the long-standing desktop cap
export const TRAY_EDGE_GUTTER_PX = 64; // 8 footer inset + 44 util button + 12 air
export const TRAY_CONTAINED_MIN_WIDTH_PX = 390; // the phone design floor
export const TRAY_FULL_BLEED_BELOW_PX = TRAY_CONTAINED_MIN_WIDTH_PX + 2 * TRAY_EDGE_GUTTER_PX;
/** The media query RaiseActionExpanded listens to — built from the same
 *  constant the resolver tests, so the two can never drift apart. */
export const TRAY_CONTAINED_MEDIA_QUERY = `(min-width: ${TRAY_FULL_BLEED_BELOW_PX}px)`;
/** Which width variant the bet/raise tray renders at a given viewport width.
 *  Pure so the flip point is unit-testable; the component reaches the same
 *  answer through TRAY_CONTAINED_MEDIA_QUERY. */
export const resolveTrayWidthVariant = (viewportWidthPx: number): 'contained' | 'full-bleed' =>
  viewportWidthPx >= TRAY_FULL_BLEED_BELOW_PX ? 'contained' : 'full-bleed';
